import React from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { InjectedStyledComponent } from '../../../../../shared/index.js';
import SortButton from '../../../SortButton/SortButton.js';
import Tooltip from '../../../Tooltip/Tooltip.js';
import Translate from '../../../Translate/Translate.js';
import { TranslationContext } from '../../../Translate/Translate.js';
import VisuallyHidden from '../../../VisuallyHidden/VisuallyHidden.js';
import { ITableEvents } from '../../models/index.js';
import HeadCellSubTitle from '../../styledComponents/HeadCellSubTitle.js';
import Superscript from '../../styledComponents/Superscript.js';
import TableDataCell from '../../styledComponents/TableDataCell.js';
import TableHeadCell from '../../styledComponents/TableHeadCell.js';
import { TableComponentBase } from '../TableComponentBase.js';

import { IHeadCellConfiguration, IHeadCellProps, IHeadCellState } from './models/index.js';
import { HEAD_CELL_CATEGORY_KEY, HEAD_CELL_COMPONENT_KEY, HEAD_CELL_PARENT_COMPONENT_KEY } from './HeadCell.meta.js';

export class HeadCell extends TableComponentBase<
	IHeadCellProps & React.ThHTMLAttributes<HTMLTableHeaderCellElement>,
	IHeadCellState,
	IHeadCellConfiguration
> {
	categoryKey = HEAD_CELL_CATEGORY_KEY;
	componentKey = HEAD_CELL_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return HEAD_CELL_PARENT_COMPONENT_KEY;
	}
	get tableEventsKey(): keyof ITableEvents {
		return 'headerCell';
	}
	constructor(props: IHeadCellProps) {
		super(props);

		makeObservable(this, {
			headerText: computed,
			superscripts: computed,
		});
	}

	get headerText() {
		const { column } = this.props;
		const { header = '', hideHeader, headerDisplay } = column;
		const headerComponent = headerDisplay ? (
			this.table.logic.getHeaderValue(headerDisplay)
		) : (
			<Translate>{header}</Translate>
		);
		if (hideHeader) {
			return <VisuallyHidden>{headerComponent}</VisuallyHidden>;
		}
		return headerComponent;
	}

	get superscripts(): (string | number)[] | undefined {
		const { column } = this.props;
		if (column && column.disclaimers) {
			return this.table.logic.getHeadCellSuperscripts(column);
		}
		return;
	}

	render() {
		const {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			children,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			configuration,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			componentStyle,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			componentStyleProps,

			table,
			rowIndex,
			column,
			columnIndex,
			...otherProps
		} = this.props;
		const { header = '', subTitle, headCellSubTitleStyle, headerDisplay } = column;
		const cellKey = this.getHeadCellKey(rowIndex, columnIndex, column);
		const tooltip = this.getTooltipHeaderProps(column, {
			target: 'header',
		});
		const subTitleTooltip = this.getTooltipHeaderProps(column, {
			target: 'subtitle',
		});
		const superscripts = this.superscripts;
		const events = this.getEvents(this.eventsForElement, undefined, rowIndex, column, columnIndex, header);
		const sortEnabled = column && column.sort && column.sort.enabled;

		let HeadCellElement = TableHeadCell;
		// if not text header, render a standard td to prevent accessibility confusion
		if (!header) {
			HeadCellElement = TableDataCell;
		}

		return (
			<TranslationContext.Consumer>
				{(translationContext) => {
					const ariaProps: any = {};
					if (!tooltip && !subTitle && !sortEnabled) {
						return (
							<HeadCellElement {...events} {...otherProps}>
								{this.headerText}
								{superscripts && <Superscript>{superscripts.join(',')}</Superscript>}
							</HeadCellElement>
						);
					}

					if (header) {
						// set up aria-label so that VO announces just the header instead of the tooltips as well
						ariaProps['aria-label'] = translationContext.translate(header);
					}
					return (
						<HeadCellElement {...events} {...otherProps}>
							<div {...ariaProps}>
								{(header || headerDisplay) && this.headerText}
								{superscripts && <Superscript>{superscripts.join(',')}</Superscript>}
								{tooltip && <Tooltip {...tooltip} />}
								{subTitle && (
									<HeadCellSubTitle
										key={`${cellKey}_subTitle`}
										componentStyle={headCellSubTitleStyle}
									>
										<Translate>{subTitle}</Translate>
										{subTitleTooltip && <Tooltip {...subTitleTooltip} />}
									</HeadCellSubTitle>
								)}
								{column && column.sort && sortEnabled && (
									<SortButton
										{...column.sort}
										payload={column}
										{...(table.sortingDirections[column.id] || {})}
										onChange={table.wrapSort(column.sort.onChange)}
									/>
								)}
							</div>
						</HeadCellElement>
					);
				}}
			</TranslationContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(observer(HeadCell), HEAD_CELL_CATEGORY_KEY, HEAD_CELL_PARENT_COMPONENT_KEY, [
	HEAD_CELL_COMPONENT_KEY,
]);
